"use client";
import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";
import { motion, Variants } from "framer-motion";
import Image from "next/image";

export default function WebusHowToUseSection() {
  const containerVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };
  return (
    <div className="py-40">
      <div className="w-full xl:max-w-[1280px] mx-auto px-5 xl:px-2  md:pt-40 pb-10 pt-5">
        <SectionTitle
          align="left"
          label={"How to Use"}
          title={"빠르고 간편하게 요청하세요"}
          description={
            <>
              복잡한 절차 없이, <br />몇 번의 터치로 저상버스 탑승을 손쉽게
              요청할 수 있습니다.
            </>
          }
        />
        <motion.div
          className="flex flex-col lg:flex-row mt-20 md:mt-40 w-full items-center justify-center gap-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* 1️⃣ 메인페이지 */}
          <motion.div
            className="flex flex-col items-center w-full max-w-3/5 lg:max-w-[310px]"
            variants={itemVariants}
          >
            <div className="relative w-full aspect-[310/670]">
              <Image
                src="/assets/images/page/service/webus/how1.png"
                alt="메인페이지"
                fill
                className="object-contain"
              />
            </div>
            <div className="text-center mt-5 md:mt-8 ">
              <p
                className="text-center text-[15px] md:text-lg text-white bg-[#5081FF] inline-flex px-3 md:px-4 py-[5px] md:py-2 rounded-full mb-3 md:mb-4 tracking-tight leading-[21px]"
                style={{ fontFamily: '"pre-Medium", sans-serif' }}
              >
                메인페이지
              </p>
              <p
                className="text-sm md:text-base tracking-tight text-midGray-50 md:leading-7 leading-[22px] "
                style={{ fontFamily: '"pre-Medium", sans-serif' }}
              >
                출발지와 도착지를 검색하거나,
                <br />
                즐겨찾기 기능으로
                <br />
                빠른 경로 확인이 가능합니다.
              </p>
            </div>
          </motion.div>

          {/* 2️⃣~4️⃣ 경로검색 / 상세지도 / 탑승요청 */}
          <motion.div
            className="relative flex flex-col lg:flex-row flex-wrap justify-center lg:flex-nowrap items-start gap-4 grow w-full"
            variants={itemVariants}
          >
            <svg
              className="absolute top-[50px] left-1/2 -translate-x-1/2 w-[70%] lg:w-[60%] h-[10px] -z-10 hidden lg:flex"
              viewBox="0 0 700 10"
            >
              {Array.from({ length: 40 }).map((_, i) => (
                <motion.circle
                  key={i}
                  cx={i * 24 + 6} // 점 간격
                  cy="5"
                  r="3" // 반지름 4px → 직경 8px
                  fill="#5081FF"
                  fillOpacity={0.64}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: i * 0.1,
                    type: "spring",
                    stiffness: 200,
                  }}
                />
              ))}
            </svg>
            {[
              {
                src: "how2.png",
                label: "경로검색",
                text: "출발지와 도착지를 입력하여 최적 이동 경로를 확인 할 수 있습니다.",
              },
              {
                src: "how3.png",
                label: "상세지도",
                text: "선택한 경로를 지도를 통해 시각적으로 확인할 수 있습니다.",
              },
              {
                src: "how4.png",
                label: "탑승요청",
                text: "사용자의 탑승 요청 정보를 버스 기사님께 전송합니다",
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                className={`flex items-center w-full max-w-full scale-[1] md:scale-[0.85] lg:scale-[0.9] md:mb-0  mb-5 ${
                  idx === 1
                    ? "flex-row-reverse lg:flex-col"
                    : "flex-row lg:flex-col"
                }`}
                variants={itemVariants}
              >
                <div className="text-center mb-10">
                  <p
                    className="text-center  text-[15px] md:text-lg text-white bg-[#5081FF] inline-flex px-3 md:px-4 py-[5px] md:py-2 rounded-full mb-3 md:mb-4 tracking-tight leading-[21px]"
                    style={{ fontFamily: '"pre-Medium", sans-serif' }}
                  >
                    {item.label}
                  </p>
                  <p
                    className="text-sm md:text-base tracking-tight text-midGray-50 md:leading-7 leading-[22px] break-keep"
                    style={{ fontFamily: '"pre-Medium", sans-serif' }}
                  >
                    {item.text}
                  </p>
                </div>
                <div className="relative w-full aspect-[152/330] md:aspect-[278/600] mx-5 md:mx-10">
                  <Image
                    src={`/assets/images/page/service/webus/${item.src}`}
                    alt={item.label}
                    fill
                    className="object-contain"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
